import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Search,
  Download,
  Upload,
  Eye,
  Trash2,
  Bell,
  User,
  Clock,
  Edit,
  MoreHorizontal,
  X,
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';
import DocumentPreviewModal from './components/DocumentPreviewModal';

interface EmployeeDocument {
  id: string;
  employeeName: string;
  employeeId: string;
  department: string;
  documentType: string;
  uploadDate: string;
  expiryDate: string;
  status: string;
  fileSize: string;
  fileName: string;
  uploadedBy: string;
  notifications?: boolean;
}

const mockDocuments: EmployeeDocument[] = [
  {
    id: '1',
    employeeName: 'Ahmed Hassan',
    employeeId: 'EMP001',
    department: 'Engineering',
    documentType: 'Passport',
    uploadDate: '2024-01-15',
    expiryDate: '2025-06-30',
    status: 'active',
    fileSize: '2.4 MB',
    fileName: 'passport_ahmed_hassan.pdf',
    uploadedBy: 'HR Department',
    notifications: true,
  },
  {
    id: '2',
    employeeName: 'Sarah Al-Zahra',
    employeeId: 'EMP002',
    department: 'Marketing',
    documentType: 'Emirates ID',
    uploadDate: '2024-02-01',
    expiryDate: '2024-03-15',
    status: 'expiring',
    fileSize: '1.8 MB',
    fileName: 'emirates_id_sarah.pdf',
    uploadedBy: 'Employee',
    notifications: true,
  },
  {
    id: '3',
    employeeName: 'Mohammed Al-Rashid',
    employeeId: 'EMP003',
    department: 'Sales',
    documentType: 'Visa',
    uploadDate: '2024-01-22',
    expiryDate: '2024-01-30',
    status: 'expired',
    fileSize: '3.2 MB',
    fileName: 'visa_mohammed.pdf',
    uploadedBy: 'HR Specialist',
    notifications: false,
  },
  {
    id: '4',
    employeeName: 'Lisa Chen',
    employeeId: 'EMP004',
    department: 'Engineering',
    documentType: 'Work Permit',
    uploadDate: '2024-02-10',
    expiryDate: '2025-12-31',
    status: 'active',
    fileSize: '1.2 MB',
    fileName: 'work_permit_lisa.pdf',
    uploadedBy: 'Legal Department',
    notifications: true,
  },
  {
    id: '5',
    employeeName: 'Omar Abdullah',
    employeeId: 'EMP005',
    department: 'Operations',
    documentType: 'Health Certificate',
    uploadDate: '2024-01-08',
    expiryDate: '2024-04-08',
    status: 'expiring',
    fileSize: '856 KB',
    fileName: 'health_cert_omar.pdf',
    uploadedBy: 'Medical Department',
    notifications: true,
  },
  {
    id: '6',
    employeeName: 'Fatima Al-Nouri',
    employeeId: 'EMP006',
    department: 'Finance',
    documentType: 'Driving License',
    uploadDate: '2024-02-05',
    expiryDate: '2026-08-15',
    status: 'active',
    fileSize: '945 KB',
    fileName: 'license_fatima.pdf',
    uploadedBy: 'Employee',
    notifications: true,
  },
  {
    id: '7',
    employeeName: 'David Kim',
    employeeId: 'EMP007',
    department: 'IT',
    documentType: 'Professional Certificate',
    uploadDate: '2024-01-30',
    expiryDate: '2025-01-30',
    status: 'active',
    fileSize: '1.5 MB',
    fileName: 'cert_david_pmp.pdf',
    uploadedBy: 'Training Department',
    notifications: true,
  },
  {
    id: '8',
    employeeName: 'Mariam Al-Zaabi',
    employeeId: 'EMP008',
    department: 'HR',
    documentType: 'Medical Insurance',
    uploadDate: '2024-02-12',
    expiryDate: '2024-12-31',
    status: 'active',
    fileSize: '678 KB',
    fileName: 'insurance_mariam.pdf',
    uploadedBy: 'Insurance Team',
    notifications: true,
  },
];

const getExpiryStatus = (expiryDate: string) => {
  const today = new Date();
  const expiry = new Date(expiryDate);
  const daysToExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (daysToExpiry < 0) {
    return { status: 'expired', color: 'bg-red-100 text-red-800', label: 'Expired' };
  } else if (daysToExpiry <= 30) {
    return { status: 'expiring', color: 'bg-amber-100 text-amber-800', label: `${daysToExpiry} days left` };
  } else {
    return { status: 'active', color: 'bg-green-100 text-green-800', label: 'Active' };
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString)?.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export default function EmployeeDocumentCenter() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('expiry-asc');
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [selectedDocument, setSelectedDocument] = useState<EmployeeDocument | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const filteredDocuments = mockDocuments
    .filter((doc) => {
      const matchesSearch =
        doc.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.documentType.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.department.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'expiry-asc':
          return new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime();
        case 'expiry-desc':
          return new Date(b.expiryDate).getTime() - new Date(a.expiryDate).getTime();
        case 'name-asc':
          return a.employeeName.localeCompare(b.employeeName);
        case 'name-desc':
          return b.employeeName.localeCompare(a.employeeName);
        default:
          return 0;
      }
    });

  const stats = {
    total: mockDocuments.length,
    active: mockDocuments.filter((d) => getExpiryStatus(d.expiryDate).status === 'active').length,
    expiring: mockDocuments.filter((d) => getExpiryStatus(d.expiryDate).status === 'expiring').length,
    expired: mockDocuments.filter((d) => getExpiryStatus(d.expiryDate).status === 'expired').length,
  };

  const toggleDocumentSelection = (documentId: string) => {
    const newSelected = new Set(selectedDocuments);
    if (newSelected.has(documentId)) {
      newSelected.delete(documentId);
    } else {
      newSelected.add(documentId);
    }
    setSelectedDocuments(newSelected);
  };

  const toggleAllSelection = () => {
    if (selectedDocuments.size === filteredDocuments.length) {
      setSelectedDocuments(new Set());
    } else {
      setSelectedDocuments(new Set(filteredDocuments.map(doc => doc.id)));
    }
  };

  const handleExport = useCallback(() => {
    setIsLoading(true);
    setTimeout(() => {
      console.log('Exporting documents...');
      setIsLoading(false);
    }, 1000);
  }, []);

  const handleBulkAction = useCallback((action: string) => {
    setIsLoading(true);
    setTimeout(() => {
      console.log(`Bulk action: ${action} for`, Array.from(selectedDocuments));
      if (action === 'bulk-delete') {
        setSelectedDocuments(new Set());
      }
      setIsLoading(false);
    }, 1000);
  }, [selectedDocuments]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <ComingSoonBanner
        icon={<FileText className="w-6 h-6" />}
        title="Employee Document Center"
        description="Employee Document Center is currently in development. This is a preview of the upcoming feature."
        gradientFrom="from-orange-500"
        gradientTo="to-amber-500"
      />

      {/* Header */}
      <div className="bg-card rounded-2xl shadow-sm border border-border p-8 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('Employee Document Center')}</h1>
              <p className="text-gray-600 mt-1">
                {t('Comprehensive tracking of all employee documents with expiry monitoring')}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <FileText className="w-4 h-4" />
            <span>{filteredDocuments.length} {t('documents')}</span>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('Total Documents')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('Active')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('Expiring Soon')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.expiring}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">{t('Expired')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.expired}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t('Search employees, document types, departments...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary w-80"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="expiry-asc">{t('Expiry Date (Earliest)')}</option>
              <option value="expiry-desc">{t('Expiry Date (Latest)')}</option>
              <option value="name-asc">{t('Employee Name A-Z')}</option>
              <option value="name-desc">{t('Employee Name Z-A')}</option>
            </select>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleExport}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span>{t('Export')}</span>
            </button>
            <button
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span>{t('Bulk Upload')}</span>
            </button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedDocuments.size > 0 && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedDocuments.size} {t('document')}{selectedDocuments.size !== 1 ? 's' : ''} {t('selected')}
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkAction('send-notifications')}
                  disabled={isLoading}
                  className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <Bell className="w-3.5 h-3.5" />
                  <span>{t('Send Notifications')}</span>
                </button>
                <button
                  onClick={() => handleBulkAction('download-selected')}
                  disabled={isLoading}
                  className="flex items-center space-x-1 px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>{t('Download')}</span>
                </button>
                <button
                  onClick={() => handleBulkAction('bulk-delete')}
                  disabled={isLoading}
                  className="flex items-center space-x-1 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>{t('Delete')}</span>
                </button>
                <button
                  onClick={() => setSelectedDocuments(new Set())}
                  className="p-1 text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Documents Table */}
      <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12 px-6 py-3">
                  <input
                    type="checkbox"
                    checked={selectedDocuments.size === filteredDocuments.length && filteredDocuments.length > 0}
                    onChange={toggleAllSelection}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary/20"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Employee')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Document Type')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Expiry Date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Upload Date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Notifications')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDocuments.map((doc) => {
                const expiryInfo = getExpiryStatus(doc.expiryDate);
                return (
                  <tr
                    key={doc.id}
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                      selectedDocuments.has(doc.id) ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => setSelectedDocument(doc)}
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedDocuments.has(doc.id)}
                        onChange={() => toggleDocumentSelection(doc.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-primary border-border rounded focus:ring-primary/20"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-500" />
                        </div>
                        <div className="ml-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('Navigate to employee:', doc.employeeName);
                            }}
                            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                          >
                            {doc.employeeName}
                          </button>
                          <div className="text-xs text-gray-500">
                            {doc.employeeId} • {doc.department}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{doc.documentType}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex flex-col">
                        <span>{formatDate(doc.expiryDate)}</span>
                        {expiryInfo.status === 'expiring' && (
                          <span className="text-xs text-amber-600 font-medium">
                            {expiryInfo.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${expiryInfo.color}`}>
                        {expiryInfo.status === 'expired' && <XCircle className="w-3 h-3 mr-1" />}
                        {expiryInfo.status === 'expiring' && <Clock className="w-3 h-3 mr-1" />}
                        {expiryInfo.status === 'active' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {expiryInfo.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex flex-col">
                        <span>{formatDate(doc.uploadDate)}</span>
                        <span className="text-xs text-gray-500">{t('by')} {doc.uploadedBy}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {doc.notifications ? (
                          <div className="flex items-center space-x-1 text-green-600">
                            <Bell className="w-3.5 h-3.5" />
                            <span className="text-xs">{t('Enabled')}</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-1 text-gray-400">
                            <Bell className="w-3.5 h-3.5" />
                            <span className="text-xs">{t('Disabled')}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDocument(doc);
                          }}
                          className="text-primary hover:text-primary/80 transition-colors"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('Download document:', doc.fileName);
                          }}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('Edit document:', doc.id);
                          }}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('More actions for:', doc.id);
                          }}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="More Actions"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Document Preview Modal */}
      {selectedDocument && (
        <DocumentPreviewModal
          document={selectedDocument}
          onClose={() => setSelectedDocument(null)}
          getExpiryStatus={getExpiryStatus}
        />
      )}
    </div>
  );
}
